import { CheckCircleIcon, ClipboardDocumentIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { Building2, CheckCircle, Copy, Mail, Phone, User } from 'lucide-react';
import { DateTime } from 'luxon';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { FC, useEffect, useRef, useState } from 'react';
import { HiInformationCircle } from 'react-icons/hi2';
import { MdDownload } from 'react-icons/md';
import { useContextSelector } from 'use-context-selector';

import { getDigitalAssetUrl } from '@/core/apis/asset-manager-apis';
import { GenerateQrCode } from '@/core/apis/bentoboard-apis';
import { authSelector } from '@/core/store/global/app-states';
import { setNotificationAction } from '@/core/store/global/app-view-reducers';
import { AppContext, setAppState } from '@/core/store/global/AppProvider';
import { geProductPagetLink } from '@/core/utils/helpers';
import { appSettings } from '@/settings/app-settings';
import { Card, CardContent } from '@/ux/components/shadcn/ui/card';
import RewardBanner from '@/ux/pages/BackOffice/components/RewardsList/RewardBanner';
import Tooltip from '@/ux-legacy/components/common/Tooltip/Tooltip';
import { LISTITEM_TYPE_BOLDER_ASSET_THIRD_PARTY } from '@/ux-legacy/experience/products/products-contstants';
type VendorOrderListingDetailProps = {
  onClose?: () => void;
  order: any;
  showHeader?: boolean;
  vendorId?: string;
  showBusiness?: boolean;
  classes?: string;
  userId?: string;
  showUser?: boolean;
};
const VendorOrderListingDetail: FC<VendorOrderListingDetailProps> = ({
  onClose,
  order,
  vendorId,
  showBusiness = false,
  showHeader = true,
  classes,
  userId,
  showUser = false,
}) => {
  const router = useRouter();
  const dispatch = useContextSelector(AppContext, setAppState);
  const [isQrCodeLoading, setIsQrCodeLoading] = useState(false);
  const [QrCode, setQrCode] = useState<string>('');
  const [showInformOf, setShowInformOf] = useState('');
  const infoButtonRef = useRef<HTMLButtonElement>(null);
  const [orderQrCode, setOrderQrCode] = useState<string>('');
  const authentication = useContextSelector(AppContext, authSelector);

  console.log(authentication, userId);
  const totalTaxAmount = order?.orderLineItemsDetails.reduce((acc: any, item: any) => {
    return acc + item.taxAmount;
  }, 0);

  const getProductNavigationlink = (product: any) => {
    // if (product?.board) {
    //   return `/portal?id=${product?.board?._id || product?.board}`;
    // } else {
    return `/product?type=${product?.productType}&productId=${product?.productId}`;
    // }
  };

  useEffect(() => {
    if (order?.guid && vendorId) {
      setIsQrCodeLoading(true);
      generateQrCodeForBoard(
        `${window.location.origin + '/orderfulfil/' + order?.guid + '?vendorId=' + vendorId}`,
      );
      generateQrCode(`${window.location.origin + '/order/' + order?.guid}`);
    }
  }, [order, vendorId]);

  function bufferToBase64(buffer: any) {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  const generateQrCodeForBoard = (url: string) => {
    GenerateQrCode(appSettings.api, url).then((res) => {
      const base64Image = bufferToBase64(res);
      setQrCode(base64Image);

      setIsQrCodeLoading(false);
    });
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const infoButton = infoButtonRef.current;
      const infoDisplay = document.getElementById(`infoDisplay_${showInformOf}`);
      if (
        infoButton &&
        !infoButton.contains(event.target as Node) &&
        (!infoDisplay || !infoDisplay.contains(event.target as Node))
      ) {
        setShowInformOf('');
      }
    }

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showInformOf]);

  const handleInfoButtonClick = (lineItemId: string) => {
    setShowInformOf((prev) => (prev === lineItemId ? '' : lineItemId));
  };

  const handleDownload = async (assetId: string) => {
    try {
      const response = await getDigitalAssetUrl(assetId);
      const a = document.createElement('a');
      a.href = response.data.url;
      a.download = '';
      a.style.display = 'none';

      document.body.appendChild(a);

      a.click();

      document.body.removeChild(a);
    } catch (err) {
      dispatch(
        setNotificationAction({
          notification: {
            show: true,
            variant: 'error',
            message: 'Unable to download asset',
          },
        }),
      );
    }
  };

  const generateQrCode = (url: string) => {
    setIsQrCodeLoading(true);
    GenerateQrCode(appSettings.api, url)
      .then((res) => {
        const base64Image = bufferToBase64(res);
        setOrderQrCode(base64Image);
      })
      .finally(() => {
        setIsQrCodeLoading(false);
      });
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      dispatch(
        setNotificationAction({
          notification: {
            show: true,
            variant: 'success',
            message: 'Copied',
          },
        }),
      );
    });
  };

  return (
    <>
      <div className={classes}>
        <h2 className="sr-only">Recent orders</h2>
        <div className="mx-auto max-w-7xl sm:px-2 lg:px-8">
          <div className="mx-auto max-w-6xl space-y-8 sm:px-4 lg:max-w-5xl lg:px-0">
            <div
              key={order?.guid}
              className="border-b  border-gray-200 bg-white shadow-sm sm:rounded-lg sm:border"
            >
              <h3 className="sr-only">
                Order placed on <time dateTime={order?.createdDatetime}>{order?.createdDate}</time>
              </h3>
              {/* <div className="flex items-center border-b border-gray-200 p-4 sm:grid sm:grid-cols-4 sm:gap-x-6 sm:p-6">
                    <dl className="grid flex-1 grid-cols-2 gap-x-6 text-sm sm:col-span-4 sm:grid-cols-4 lg:col-span-2"></dl> */}
              {/* <div className="flex sm:items-center border-b border-gray-200 p-4 sm:grid sm:gap-x-3 sm:p-6 items-start">
                <dl className="grid md:flex justify-between flex-1 grid-cols-1 gap-6 text-sm sm:col-span-0  lg:col-span-2">
                  <div className="">
                    <dt className="font-medium text-gray-900">Order ID</dt>
                    <dd className="mt-1 text-gray-500">
                      {order?.shortId?.split('/').pop()}
                      {order?.isTest === 'true' && <span className="ml-1">(Test)</span>}
                    </dd>
                  </div>
                  {order?.invoiceDetails?.shortInvoiceId && (
                    <div className="">
                      <dt className="font-medium text-gray-900">Invoice ID</dt>
                      <dd className="mt-1 text-gray-500">
                        <button
                          className="text-blue-500 underline"
                          onClick={() => router.push(`/invoice/${order?.invoiceDetails?.guid}`)}
                        >
                          {order?.invoiceDetails?.shortInvoiceId?.split('/')?.[2]}
                        </button>
                      </dd>
                    </div>
                  )}
                  <div className="">
                    <dt className="font-medium text-gray-900">Order Date</dt>
                    <dd className="mt-1 text-gray-500">
                      {order?.createdAt ? (
                        <time dateTime={order?.createdAt}>
                          {format(new Date(order?.createdAt), 'yyyy-MM-dd')}
                        </time>
                      ) : (
                        <span>No date available</span>
                      )}
                    </dd>
                  </div>
                  <div className="">
                    <dt className="font-medium text-gray-900">Amount</dt>
                    <dd className="mt-1 font-medium text-gray-500">
                      {order?.totalAmount !== 0 && <span className="mr-1">{order?.currencyCode}</span>}
                      {order?.totalAmount.toFixed(2)}
                    </dd>
                  </div>
                  {(userId || showUser) && (
                    <div className="">
                      <dt className="font-medium text-gray-900">Customer</dt>
                      <dd className="mt-1 font-medium text-gray-500">
                        {order?.userDetails?.Name && (
                          <div className="flex items-center">
                            <>
                              <FaUser className="mr-1 " />
                              <div
                                className="mr-1"
                                style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                              >
                                {order?.userDetails?.Name}
                              </div>
                            </>
                          </div>
                        )}
                        {order?.userDetails?.Email && (
                          <div className="flex items-center">
                            <ClipboardDocumentIcon
                              className="md:-ml-6 mr-1 h-5 w-5 text-gray-600 cursor-pointer hover:text-indigo-600"
                              onClick={() => {
                                navigator.clipboard
                                  .writeText(order?.userDetails?.Email)
                                  .then(() => {
                                    dispatch(
                                      setNotificationAction({
                                        notification: {
                                          show: true,
                                          variant: 'success',
                                          message: 'Copied',
                                        },
                                      }),
                                    );
                                    setTimeout(() => {
                                      dispatch(
                                        setNotificationAction({
                                          notification: {
                                            show: false,
                                            variant: '',
                                            message: '',
                                          },
                                        }),
                                      );
                                    }, 3000);
                                  })
                                  .catch((error) => {
                                    console.error('Error copying text: ', error);
                                  });
                              }}
                            />
                            <FaEnvelope className="mr-1  sm:w-auto sm:h-auto" />
                            <div
                              className="mr-1"
                              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {order?.userDetails?.Email}
                            </div>
                          </div>
                        )}
                        {order?.phone && (
                          <div className="flex items-center">
                            <ClipboardDocumentIcon
                              className="md:-ml-6 mr-1 md:h-5 md:w-5 h-5 w-5 text-gray-600 cursor-pointer hover:text-indigo-600"
                              onClick={() => {
                                navigator.clipboard
                                  .writeText(order?.phone)
                                  .then(() => {
                                    dispatch(
                                      setNotificationAction({
                                        notification: {
                                          show: true,
                                          variant: 'success',
                                          message: 'Copied',
                                        },
                                      }),
                                    );
                                    setTimeout(() => {
                                      dispatch(
                                        setNotificationAction({
                                          notification: {
                                            show: false,
                                            variant: '',
                                            message: '',
                                          },
                                        }),
                                      );
                                    }, 3000);
                                  })
                                  .catch((error) => {
                                    console.error('Error copying text: ', error);
                                  });
                              }}
                            />
                            <FaPhone className="mr-1 w-3 h-3 md:w-auto md:h-auto" />

                            <div
                              className="mr-1"
                              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {order?.phone}
                            </div>
                          </div>
                        )}
                      </dd>
                    </div>
                  )}
                  {showBusiness && !showUser && (
                    <div>
                      <dt className="font-medium text-gray-900">Business</dt>
                      <dd className="mt-1 font-medium text-gray-500">
                        {order?.vendorDetails?.businessName && (
                          <div className="flex items-center -mt-[5px] sm:-mt-0">
                            <FaUser className="mr-1  sm:h-auto sm:w-auto " />

                            <div
                              className="mr-1"
                              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {order?.vendorDetails?.businessName}
                            </div>
                          </div>
                        )}
                        {order?.vendorDetails?.businessEmail && (
                          <div className="flex items-center ">
                            <ClipboardDocumentIcon
                              className="md:-ml-6 mr-1 h-5 w-5 text-gray-600 cursor-pointer hover:text-indigo-600"
                              onClick={() => {
                                navigator.clipboard
                                  .writeText(order?.vendorDetails?.businessEmail)
                                  .then(() => {
                                    dispatch(
                                      setNotificationAction({
                                        notification: {
                                          show: true,
                                          variant: 'success',
                                          message: 'Copied',
                                        },
                                      }),
                                    );
                                    setTimeout(() => {
                                      dispatch(
                                        setNotificationAction({
                                          notification: {
                                            show: false,
                                            variant: '',
                                            message: '',
                                          },
                                        }),
                                      );
                                    }, 3000);
                                  })
                                  .catch((error) => {
                                    console.error('Error copying text: ', error);
                                  });
                              }}
                            />

                            <FaEnvelope className="mr-1 sm:w-auto sm:h-auto" />

                            <div
                              className="mr-1"
                              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {order?.vendorDetails?.businessEmail}
                            </div>
                          </div>
                        )}
                        {order?.vendorDetails?.businessContactNumber && (
                          <div className="flex items-center ">
                            <ClipboardDocumentIcon
                              className="md:-ml-6 mr-1 h-5 w-5 text-gray-600 cursor-pointer hover:text-indigo-600"
                              onClick={() => {
                                navigator.clipboard
                                  .writeText(
                                    order?.vendorDetails?.countryCode +
                                      order?.vendorDetails?.businessContactNumber,
                                  )
                                  .then(() => {
                                    dispatch(
                                      setNotificationAction({
                                        notification: {
                                          show: true,
                                          variant: 'success',
                                          message: 'Copied',
                                        },
                                      }),
                                    );
                                    setTimeout(() => {
                                      dispatch(
                                        setNotificationAction({
                                          notification: {
                                            show: false,
                                            variant: '',
                                            message: '',
                                          },
                                        }),
                                      );
                                    }, 3000);
                                  })
                                  .catch((error) => {
                                    console.error('Error copying text: ', error);
                                  });
                              }}
                            />
                            <FaPhone className="mr-1 sm:w-3 sm:h-3 md:w-auto md:h-auto" />
                            <div
                              className="mr-1"
                              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {order?.vendorDetails?.countryCode}
                              {order?.vendorDetails?.businessContactNumber}
                            </div>
                          </div>
                        )}
                      </dd>
                    </div>
                  )}

                  {order?.address && (
                    <div className="md:hidden">
                      <dt className="font-medium text-gray-900">Address</dt>
                      <dd className="mt-1 font-medium text-gray-500">
                        {order?.address?.Line1 && (
                          <div className="-mt-[5px] sm:-mt-0">
                            <div
                              className="mr-1"
                              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {order?.address?.Line1} {','}
                            </div>

                            <div
                              className="mr-1"
                              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {order?.address?.Line2} {','}
                            </div>

                            <div
                              className="mr-1"
                              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {order?.address?.City} {','}
                            </div>

                            <div
                              className="mr-1"
                              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {order?.address?.State} {','}
                            </div>

                            <div
                              className="mr-1"
                              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {order?.address?.Country} {','}
                            </div>

                            <div
                              className="mr-1"
                              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {'ZipCode: '} {order?.address?.ZipCode}
                            </div>
                          </div>
                        )}
                      </dd>
                    </div>
                  )}

                  {order?.paymentDetails?.paidByEmail &&
                    order?.paymentDetails?.paidByEmail !== order?.userDetails?.Email && (
                      <div className="">
                        <dt className="font-medium text-gray-900">Paid by</dt>
                        <dd className="mt-1 font-medium text-gray-500">
                          {order?.paymentDetails?.paidByName && (
                            <div className="flex items-center">
                              <>
                                <FaUser className="mr-1 " />
                                <div
                                  className="mr-1"
                                  style={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {order?.paymentDetails?.paidByName}
                                </div>
                              </>
                            </div>
                          )}
                          {order?.paymentDetails?.paidByEmail && (
                            <div className="flex items-center">
                              <ClipboardDocumentIcon
                                className="md:-ml-6 mr-1 h-5 w-5 text-gray-600 cursor-pointer hover:text-indigo-600"
                                onClick={() => {
                                  navigator.clipboard
                                    .writeText(order?.paymentDetails?.paidByEmail)
                                    .then(() => {
                                      dispatch(
                                        setNotificationAction({
                                          notification: {
                                            show: true,
                                            variant: 'success',
                                            message: 'Copied',
                                          },
                                        }),
                                      );
                                      setTimeout(() => {
                                        dispatch(
                                          setNotificationAction({
                                            notification: {
                                              show: false,
                                              variant: '',
                                              message: '',
                                            },
                                          }),
                                        );
                                      }, 3000);
                                    })
                                    .catch((error) => {
                                      console.error('Error copying text: ', error);
                                    });
                                }}
                              />
                              <FaEnvelope className="mr-1 sm:w-auto sm:h-auto" />
                              <div
                                className="mr-1"
                                style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                              >
                                {order?.paymentDetails?.paidByEmail}
                              </div>
                            </div>
                          )}
                          {order?.paymentDetails?.paidByPhone && (
                            <div className="flex items-center">
                              <ClipboardDocumentIcon
                                className="md:-ml-6 mr-1 md:h-5 md:w-5 h-5 w-5 text-gray-600 cursor-pointer hover:text-indigo-600"
                                onClick={() => {
                                  navigator.clipboard
                                    .writeText(order?.paymentDetails?.paidByPhone)
                                    .then(() => {
                                      dispatch(
                                        setNotificationAction({
                                          notification: {
                                            show: true,
                                            variant: 'success',
                                            message: 'Copied',
                                          },
                                        }),
                                      );
                                      setTimeout(() => {
                                        dispatch(
                                          setNotificationAction({
                                            notification: {
                                              show: false,
                                              variant: '',
                                              message: '',
                                            },
                                          }),
                                        );
                                      }, 3000);
                                    })
                                    .catch((error) => {
                                      console.error('Error copying text: ', error);
                                    });
                                }}
                              />
                              <FaPhone className="mr-1 w-3 h-3 md:w-auto md:h-auto" />

                              <div
                                className="mr-1"
                                style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                              >
                                {order?.paymentDetails?.paidByPhone}
                              </div>
                            </div>
                          )}
                        </dd>
                      </div>
                    )}
                  <img
                    src={`data:image/png;base64,${orderQrCode}`}
                    className="w-[100px] h-[100px] my-2 rounded-md"
                    onLoad={() => setIsQrCodeLoading(false)}
                    alt="qrcode"
                  ></img>
                </dl>
                <div className="md:w-full md:flex ">
                  {order?.address && (
                    <div className="hidden md:block">
                      <dt className="font-medium text-gray-900">Address</dt>
                      <dd className="mt-1 font-medium text-gray-500">
                        {order?.address?.Line1 && (
                          <div className="-mt-[5px] sm:-mt-0">
                            <div
                              className="mr-1"
                              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {order?.address?.Line1} {','}
                            </div>

                            <div
                              className="mr-1"
                              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {order?.address?.Line2} {','}
                            </div>

                            <div
                              className="mr-1"
                              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {order?.address?.City} {','}
                            </div>

                            <div
                              className="mr-1"
                              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {order?.address?.State} {','}
                            </div>

                            <div
                              className="mr-1"
                              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {order?.address?.Country} {','}
                            </div>

                            <div
                              className="mr-1"
                              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {'ZipCode: '} {order?.address?.ZipCode}
                            </div>
                          </div>
                        )}
                      </dd>
                    </div>
                  )}

                  {vendorId && (
                    <>
                      {isQrCodeLoading ? (
                        <div className="w-[150px] h-[150px] border-2 rounded-md flex items-center justify-center animate-pulse">
                          <Loader />
                        </div>
                      ) : (
                        <div>
                          <img
                            src={`data:image/png;base64,${QrCode}`}
                            className="w-[100px] h-[100px] my-2 rounded-md"
                            onLoad={() => setIsQrCodeLoading(false)}
                            alt="qrcode"
                          ></img>
                          <div className=" sm:flex items-center">
                            <div className="flex items-center justify-center">
                              {order?.status === 'CONFIRMED' ? (
                                <>
                                  <CheckCircleIcon className="h-5 w-5 text-green-500" aria-hidden="true" />
                                  <p className="ml-2 text-sm font-medium text-gray-500">{order?.status}</p>
                                </>
                              ) : (
                                <>
                                  <ExclamationCircleIcon
                                    className="h-5 w-5 text-yellow-500"
                                    aria-hidden="true"
                                  />
                                  <p className="ml-2 text-sm font-medium text-gray-500">{order?.status}</p>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div> */}

              <Card className="w-full max-w-7xl mx-auto">
                <CardContent className="p-6 flex md:block justify-between">
                  <div className="space-y-6">
                    {/* First Line */}
                    <div className="grid grid-cols-1 md:grid-cols-none md:flex md:justify-between gap-6 ">
                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground">Order ID</h3>
                        <p className="mt-1 font-medium">
                          {order?.shortId?.split('/').pop()}
                          {order?.isTest === 'true' && <span className="ml-1">(Test)</span>}
                        </p>
                      </div>

                      {order?.invoiceDetails?.shortInvoiceId && (
                        <div>
                          <h3 className="text-sm font-medium text-muted-foreground">Invoice ID</h3>
                          <button
                            className="mt-1 font-medium text-blue-500 underline"
                            onClick={() => router.push(`/invoice/${order?.invoiceDetails?.guid}`)}
                          >
                            {order?.invoiceDetails?.shortInvoiceId?.split('/')?.[2]}
                          </button>
                        </div>
                      )}

                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground">Date</h3>
                        {order?.createdAt ? (
                          <p className="mt-1 font-medium">
                            {format(new Date(order?.createdAt), 'yyyy-MM-dd')}
                          </p>
                        ) : (
                          <p className="mt-1 font-medium">No date available</p>
                        )}
                      </div>

                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground">Amount</h3>
                        <p className="mt-1 font-medium">
                          {order?.totalAmount !== 0 && <span className="mr-1">{order?.currencyCode}</span>}
                          {order?.totalAmount.toFixed(2)}
                        </p>
                      </div>

                      {showBusiness && !showUser && (
                        <div>
                          <h3 className="text-sm font-medium text-muted-foreground mb-3">Business</h3>

                          <div className="space-y-2">
                            {order?.vendorDetails?.businessName && (
                              <div className="flex items-center gap-2">
                                <Building2 className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm truncate w-40">
                                  {order?.vendorDetails?.businessName}
                                </span>
                              </div>
                            )}

                            {order?.vendorDetails?.businessEmail && (
                              <div className="flex items-center gap-2 group">
                                <Mail className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">{order?.vendorDetails?.businessEmail}</span>
                                <button
                                  className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={() => handleCopy(order?.vendorDetails?.businessEmail)}
                                >
                                  <Copy className="h-3 w-3" />
                                </button>
                              </div>
                            )}

                            {order?.vendorDetails?.businessContactNumber && (
                              <div className="flex items-center gap-2 group">
                                <Phone className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">{order?.vendorDetails?.businessContactNumber}</span>
                                <button
                                  className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={() => handleCopy(order?.vendorDetails?.businessContactNumber)}
                                >
                                  <Copy className="h-3 w-3" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="md:flex hidden flex-col items-center">
                        <img
                          src={`data:image/png;base64,${orderQrCode}`}
                          alt="Order QR Code"
                          width={80}
                          height={80}
                          className="rounded-md"
                          onLoad={() => setIsQrCodeLoading(false)}
                        ></img>
                        <div className="flex items-center gap-1.5">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="text-sm font-medium">Confirmed</span>
                        </div>
                      </div>
                    </div>

                    {/* Second Line */}
                    <div
                      className={`grid grid-cols-1 md:grid-cols-3 gap-6 ${
                        userId || showUser || order?.address || order?.businessDetails?.name
                          ? 'md:border-t pt-6'
                          : ''
                      }`}
                    >
                      {order?.paymentDetails?.paidByEmail &&
                        order?.paymentDetails?.paidByEmail !== order?.userDetails?.Email && (
                          <div>
                            <h3 className="text-sm font-medium text-muted-foreground">Paid By</h3>
                            <div className="mt-1 space-y-1">
                              {order?.paymentDetails?.paidByName && (
                                <div className="flex items-center gap-2">
                                  <User className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-sm">{order?.paymentDetails?.paidByName}</span>
                                </div>
                              )}
                              {order?.paymentDetails?.paidByEmail && (
                                <div className="flex items-center gap-2 group">
                                  <Mail className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-sm">{order?.paymentDetails?.paidByEmail}</span>
                                  <button
                                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() => handleCopy(order?.paymentDetails?.paidByEmail)}
                                  >
                                    <Copy className="h-3 w-3" />
                                  </button>
                                </div>
                              )}
                              {order?.paymentDetails?.paidByPhone && (
                                <div className="flex items-center gap-2 group">
                                  <Phone className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-sm">{order?.paymentDetails?.paidByPhone}</span>
                                  <button
                                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() => handleCopy(order?.paymentDetails?.paidByPhone)}
                                  >
                                    <Copy className="h-3 w-3" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                      {(userId || showUser) && (
                        <div>
                          <h3 className="text-sm font-medium text-muted-foreground mb-3">Customer</h3>

                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">
                                {order?.userDetails?.Name || order?.userDetails?.Handle}
                              </span>
                            </div>

                            <div className="flex items-center gap-2 group">
                              <Mail className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">{order?.userDetails?.Email}</span>
                              <button
                                className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={() => handleCopy(order?.userDetails?.Email)}
                              >
                                <Copy className="h-3 w-3" />
                              </button>
                            </div>

                            {order?.phone && (
                              <div className="flex items-center gap-2 group">
                                <Phone className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">{order?.phone}</span>
                                <button
                                  className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={() => handleCopy(order?.phone)}
                                >
                                  <Copy className="h-3 w-3" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {order?.address && (
                        <div>
                          <h3 className="text-sm font-medium text-muted-foreground mb-3">Address</h3>
                          <div className="space-y-1 text-sm">
                            {order?.address?.Line1 && <p>{order.address.Line1}</p>}
                            {order?.address?.Line2 && <p>{order.address.Line2}</p>}
                            {(order?.address?.City || order?.address?.State || order?.address?.Country) && (
                              <p>
                                {[order.address.City, order.address.State, order.address.Country]
                                  .filter(Boolean)
                                  .join(', ')}
                              </p>
                            )}
                            {order?.address?.ZipCode && <p>ZipCode: {order.address.ZipCode}</p>}
                          </div>
                        </div>
                      )}

                      {order?.businessDetails?.name && (
                        <div>
                          <h3 className="text-sm font-medium text-muted-foreground mb-3">
                            Customer Business
                          </h3>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">{order?.businessDetails?.name}</span>
                            </div>

                            {/* <div className="flex items-center gap-2 group">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">business@example.com</span>
                            <button
                              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                              // onClick={() => handleCopy('business@example.com')}
                            >
                              <Copy className="h-3 w-3" />
                            </button>
                          </div> */}

                            {/* <div className="flex items-center gap-2 group">
                            <Phone className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">+1 234 567 890</span>
                            <button
                              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                              // onClick={() => handleCopy('+1 234 567 890')}
                            >
                              <Copy className="h-3 w-3" />
                            </button>
                          </div> */}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex md:hidden flex-col items-center">
                    <img
                      src={`data:image/png;base64,${orderQrCode}`}
                      alt="Order QR Code"
                      width={80}
                      height={80}
                      className="rounded-md"
                      onLoad={() => setIsQrCodeLoading(false)}
                    ></img>
                    <div className="flex items-center gap-1.5">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm font-medium">Confirmed</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {order?.isRewardAvailable && order?.reward && <RewardBanner reward={order?.reward} />}

              {/* Products */}
              <h4 className="sr-only">Items</h4>
              <ul className="divide-y divide-gray-200">
                {order?.orderLineItemsDetails?.map((product: any, index: any) => {
                  // Check if productImage is a valid URL
                  const isImageLink = /^https?:\/\/\S+\.(png|jpg|jpeg|gif|webp)$/i.test(
                    product?.productImage,
                  );

                  return (
                    <li key={index} className="p-4 sm:p-6">
                      <div className="flex items-center sm:items-start">
                        <div
                          role="button"
                          tabIndex={0}
                          className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg bg-gray-200 sm:h-24 sm:w-24"
                          onClick={() => {
                            router.push(geProductPagetLink(product?.productType, product?.productId));
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              router.push(geProductPagetLink(product?.productType, product?.productId));
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          {isImageLink ? (
                            <img
                              src={product?.productImage}
                              alt={product?.productName}
                              className="h-full w-full object-cover object-center"
                            />
                          ) : (
                            <img
                              src={appSettings.api.S3_MEDIA_COMPRESSED + product?.productImage}
                              alt={product?.productName}
                              className="h-full w-full object-cover object-center"
                            />
                          )}
                        </div>

                        <div className="ml-6 flex-1 text-sm">
                          <div className="font-medium text-gray-900 sm:flex sm:justify-between ">
                            <div>
                              <div>
                                <h5>
                                  <a
                                    href={
                                      'https://' +
                                      window.location.host +
                                      geProductPagetLink(product?.productType, product?.productId)
                                    }
                                    className="text-blue-500 hover:underline"
                                  >
                                    {product?.productName}
                                  </a>
                                </h5>
                                <div className="text-gray-400">
                                  {product.date && (
                                    <div className="flex flex-col mt-1 mb-1">
                                      <p className="text-sm font-bold">{product.date?.split('T')[0]}</p>

                                      <p className="text-sm font-bold">{product.time}</p>
                                    </div>
                                  )}
                                </div>
                                {product?.versionDetails?.sessions?.[0]?.startDate &&
                                  product?.versionDetails?.sessions?.[0]?.startTime &&
                                  product?.versionDetails?.sessions?.[
                                    product?.versionDetails?.sessions?.length - 1
                                  ]?.startDate &&
                                  product?.versionDetails?.sessions?.[
                                    product?.versionDetails?.sessions?.length - 1
                                  ]?.endTime && (
                                    <>
                                      <span>
                                        {`Date(s) : `}
                                        <span className="font-bold">
                                          {product?.versionDetails?.sessions?.[
                                            product?.versionDetails?.sessions?.length - 1
                                          ]?.startDate?.split('T')[0] !=
                                          product?.versionDetails?.sessions?.[0]?.startDate?.split('T')[0]
                                            ? `${DateTime.fromISO(
                                                product?.versionDetails?.sessions?.[0]?.startDate?.split(
                                                  'T',
                                                )[0],
                                              ).toFormat('LLL dd, yyyy')} - ${DateTime.fromISO(
                                                product?.versionDetails?.sessions?.[
                                                  product?.versionDetails?.sessions?.length - 1
                                                ]?.startDate?.split('T')[0],
                                              ).toFormat('LLL dd, yyyy')} `
                                            : `${DateTime.fromISO(
                                                product?.versionDetails?.sessions?.[0]?.startDate?.split(
                                                  'T',
                                                )[0],
                                              ).toFormat('LLL dd, yyyy')}`}
                                        </span>
                                      </span>
                                      <br />
                                    </>
                                  )}
                                {product?.versionDetails?.sessions?.[0]?.startDate &&
                                  product?.versionDetails?.sessions?.[0]?.startTime &&
                                  product?.versionDetails?.sessions?.[
                                    product?.versionDetails?.sessions?.length - 1
                                  ]?.startDate &&
                                  product?.versionDetails?.sessions?.[
                                    product?.versionDetails?.sessions?.length - 1
                                  ]?.endTime && (
                                    <span>
                                      {'Time : '}
                                      <span className="font-bold">
                                        {` ${product?.versionDetails?.sessions?.[0]?.startTime} - 
                        ${
                          product?.versionDetails?.sessions?.[product?.versionDetails?.sessions?.length - 1]
                            ?.endTime
                        }`}
                                      </span>
                                    </span>
                                  )}
                                {product?.versionDetails?.startDate &&
                                  product?.versionDetails?.endDate &&
                                  product?.productType == 5 && (
                                    <p className=" text-[#323232] text-sm">
                                      {`Date(s) : `}
                                      {DateTime.fromISO(product?.versionDetails?.startDate).toFormat(
                                        'LLL dd, yyyy',
                                      )}{' '}
                                      to{' '}
                                      {DateTime.fromISO(product?.versionDetails?.endDate).toFormat(
                                        'LLL dd, yyyy',
                                      )}
                                    </p>
                                  )}
                                {product?.versionDetails?.option1 && (
                                  <div>
                                    {/* {product?.versionDetails?.option1?.label}: */}
                                    {/* <span className="ml-1"> */}
                                    {product?.versionDetails?.option1?.value}
                                    {/* </span> */}
                                  </div>
                                )}
                                {product?.versionDetails?.option2 && (
                                  <div>
                                    {product?.versionDetails?.option2?.label}:
                                    <span className="ml-1">{product?.versionDetails?.option2?.value}</span>
                                  </div>
                                )}
                                {product?.versionDetails?.option3 && (
                                  <div>
                                    {product?.versionDetails?.option3?.label}:
                                    <span className="ml-1">{product?.versionDetails?.option3?.value}</span>
                                  </div>
                                )}
                                {product?.versionDetails?.duration && (
                                  <div className="text-sm text-gray-400">
                                    Duration:
                                    <span className="ml-1">{product?.versionDetails?.duration?.label}</span>
                                  </div>
                                )}
                                <div>
                                  {product.quantity > 0 && (
                                    <p className="mt-1 text-sm font-bold text-gray-400 ">
                                      Quantity {product?.quantity}
                                    </p>
                                  )}
                                  {product.noOfAdults > 0 && (
                                    <p className="mt-1 text-sm font-bold text-gray-400 ">
                                      Adults {product?.noOfAdults}
                                    </p>
                                  )}

                                  {product.noOfChildren > 0 && (
                                    <p className="mt-1 text-sm font-bold text-gray-400 ">
                                      Kids {product?.noOfChildren}
                                    </p>
                                  )}
                                </div>
                                {product.taxAmount > 0 && (
                                  <p className="text-sm font-bold text-gray-400">
                                    {`Tax${
                                      product?.taxPercentage > 0 ? `(${product?.taxPercentage}%)` : ``
                                    } : `}
                                    {product.currency} {parseFloat(product.taxAmount).toFixed(2)}
                                  </p>
                                )}
                              </div>
                            </div>
                            {product.productType == LISTITEM_TYPE_BOLDER_ASSET_THIRD_PARTY && (
                              <button
                                onClick={() => handleDownload(product.productId)}
                                className="w-fit h-fit py-1 flex gap-x-1 items-center text-slate-700 hover:text-blue-400"
                              >
                                <MdDownload className="h-4 w-4 " />
                                <span>Download</span>
                              </button>
                            )}
                            {product.lotteryDetails?.length > 0 && (
                              <Link
                                className="border rounded-md p-2 bg-gray-100 flex items-center justify-center text-center font-semibold"
                                href={`/lotteries-unlocked?orderId=${order.guid}`}
                              >
                                View Lottery
                              </Link>
                            )}
                            <p className="mt-2 sm:mt-0 flex">
                              {product?.originalPrice > 0 && product?.discountPercentage && (
                                <span className="mr-1" style={{ textDecoration: 'line-through' }}>
                                  {product?.currency} {product?.originalPrice}
                                </span>
                              )}
                              {product?.discountPercentage && (
                                <Tooltip
                                  text={product?.discountDescription || ''}
                                  position={'right-0 top-0 translate-x-[100%] -translate-y-1/2'}
                                  tooltipClassNames={
                                    'bg-black text-[14px] w-auto  transform transition-transform duration-500 text-white py-1 px-2 rounded-md z-50'
                                  }
                                >
                                  <span className="mr-1 sm:text-blue-500 hover:underline">
                                    ({product?.discountPercentage}% Off)
                                  </span>
                                </Tooltip>
                              )}
                              {product?.price !== 0 && <span className="mr-1">{product?.currency}</span>}
                              {product?.price}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="mt-6 sm:flex items-center gap-x-5 ">
                        <div className="flex items-center ">
                          {order?.status === 'CONFIRMED' ? (
                            <>
                              <CheckCircleIcon className="h-5 w-5 text-green-500" aria-hidden="true" />
                              <p className="ml-2 text-sm font-medium text-gray-500">{order?.status}</p>
                            </>
                          ) : (
                            <>
                              <ExclamationCircleIcon className="h-5 w-5 text-yellow-500" aria-hidden="true" />
                              <p className="ml-2 text-sm font-medium text-gray-500">{order?.status}</p>
                            </>
                          )}
                        </div>

                        {/* <div className="mt-6 flex items-center space-x-4 divide-x divide-gray-200 border-t border-gray-200 pt-4 text-sm font-medium sm:ml-4 sm:mt-0 sm:border-none sm:pt-0">
                          <div className="flex flex-1 justify-center">
                            <button
                              className="whitespace-nowrap text-indigo-600 hover:text-indigo-500"
                              onClick={() => {
                                router.push(
                                  `/product?type=${product?.productType}&productId=${product?.productId}`,
                                );
                              }}
                            >
                              View product
                            </button>
                          </div>
                         
                        </div> */}
                        {/* {product?.fulfillment === 'FULFILLED' && (
                        <img
                              src={appSettings?.images?.fulfulled}
                              alt={product?.productName}
                              className="w-8 h-8 object-cover object-center ml-5"
                            />
                        )} */}
                        <div className="flex items-center w-full">
                          {
                            product?.fulfillment_status === 'FULFILLED' && (
                              <>
                                <CheckCircleIcon
                                  className="sm:h-5 sm:w-5 h-7 w-7 text-green-500"
                                  aria-hidden="true"
                                />
                                <p className="ml-2 text-sm font-medium text-gray-500">FULFILLED</p>
                                <div className="w-full relative flex">
                                  <button
                                    className="infoButton"
                                    ref={infoButtonRef}
                                    onClick={() => handleInfoButtonClick(product?.lineItemId)}
                                  >
                                    <HiInformationCircle className="ml-2 h-5 w-5" />
                                  </button>
                                  {showInformOf === product?.lineItemId && product?.fulfillmentDetails && (
                                    <div
                                      id={`infoDisplay_${product?.lineItemId}`}
                                      className="absolute bg-black border text-white ml-7 p-2 top-0 rounded-md"
                                    >
                                      <p>
                                        Created By:{' '}
                                        <span className="text-center">
                                          {product?.fulfillmentDetails?.Name ||
                                            product?.fulfillmentDetails?.Email?.split('@')[0]}
                                        </span>
                                      </p>
                                      <p>
                                        Created At:{' '}
                                        {format(
                                          new Date(product?.fulfillmentDetails?.fulfilledAt),
                                          'yyyy-MM-dd HH:mm:ss',
                                        )}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </>
                            )
                            //  : (
                            //   <>
                            //     <ExclamationCircleIcon className="h-5 w-5 text-yellow-500" aria-hidden="true" />
                            //     <p className="ml-2 text-sm font-medium text-gray-500">UNFULFILLED</p>
                            //   </>
                            // )
                          }
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
              {order?.participantInfo?.length > 0 && (
                <div className="my-4">
                  <h2 className="text-lg font-medium mb-4 ml-[4%]">Participants</h2>
                  <div className="w-full flex justify-center">
                    <div className="border w-[95%] rounded-lg overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead>
                          <tr>
                            <th
                              scope="col"
                              className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 w-1/3"
                            >
                              Name
                            </th>
                            <th
                              scope="col"
                              className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 w-1/3"
                            >
                              Email
                            </th>
                            <th
                              scope="col"
                              className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 w-1/3"
                            >
                              Gender
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {order?.participantInfo?.map((participant: any, planIdx: any) => (
                            <tr key={planIdx}>
                              <td
                                className="py-3.5 pl-4 pr-3 text-left text-sm font-medium text-gray-900 sm:pl-6 w-1/3"
                                key={planIdx}
                              >
                                <div className="truncate w-56 overflow-hidden text-ellipsis whitespace-nowrap">
                                  {participant?.name}
                                </div>
                              </td>
                              <td className="py-3.5 pl-4 pr-3 text-left text-sm font-medium text-gray-900 sm:pl-6 w-1/3">
                                <div className="truncate w-56 overflow-hidden text-ellipsis whitespace-nowrap">
                                  {participant?.email || '-'}
                                </div>
                              </td>
                              <td className="py-3.5 pl-4 pr-3 text-left text-sm font-medium text-gray-900 sm:pl-6 w-1/3">
                                {participant?.gender
                                  ? participant?.gender?.charAt(0).toUpperCase() + participant.gender.slice(1)
                                  : ''}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="ml-[4%] mt-2">
                    Consent for Photos: <b>{order?.consentForPhotos ? 'Yes' : 'No'}</b>
                  </div>
                </div>
              )}

              <section aria-labelledby="summary-heading" className="">
                <h2 id="summary-heading" className="sr-only">
                  Billing Summary
                </h2>

                <div className="bg-gray-100 px-4 py-6 sm:rounded-lg sm:px-6 lg:grid lg:grid-cols-12 lg:gap-x-8 lg:px-8 lg:py-8">
                  <dl className=" gap-6 text-sm sm:grid-cols-2 md:gap-x-8 lg:col-span-7">
                    {order?.paymentDetails?.intent && (
                      <div>
                        <dt className="font-medium text-gray-900">Payment Reference Id</dt>
                        <dd className="mt-3 text-gray-500 flex items-center">
                          <span className="block">{order?.paymentDetails?.intent}</span>
                          <ClipboardDocumentIcon
                            className="ml-2 h-5 w-5 text-gray-600 cursor-pointer hover:text-indigo-600"
                            onClick={() => {
                              navigator.clipboard
                                .writeText(order?.paymentDetails?.intent)
                                .then(() => {
                                  dispatch(
                                    setNotificationAction({
                                      notification: {
                                        show: true,
                                        variant: 'success',
                                        message: 'Copied',
                                      },
                                    }),
                                  );
                                  setTimeout(() => {
                                    dispatch(
                                      setNotificationAction({
                                        notification: {
                                          show: false,
                                          variant: '',
                                          message: '',
                                        },
                                      }),
                                    );
                                  }, 3000);
                                })
                                .catch((error) => {
                                  console.error('Error copying text: ', error);
                                });
                            }}
                          />
                        </dd>
                      </div>
                    )}
                    {order?.paymentType && order?.paymentType !== 'FREE' && (
                      <div>
                        <dt className="font-medium text-gray-900 mt-3">Payment Mode</dt>
                        <dd className="mt-3 text-gray-500">
                          <span className="">{order?.paymentType}</span>
                        </dd>
                      </div>
                    )}
                    {order?.platformFee > 0 && (
                      <div>
                        <dt className="font-medium text-gray-900 mt-3">Platform Fee</dt>
                        <dd className="mt-3 text-gray-500">
                          <span className="">{order?.currencyCode}</span>
                          <span className="ml-2">{order?.platformFee.toFixed(2)}</span>
                        </dd>
                      </div>
                    )}

                    <div>
                      <dt className="mt-3 font-medium text-gray-900">Date</dt>
                      <dd className="mt-3 text-gray-500">
                        <span className="block">
                          {order?.createdAt ? (
                            <time dateTime={order?.createdAt}>
                              {format(new Date(order?.createdAt), 'yyyy-MM-dd HH:mm:ss')}
                            </time>
                          ) : (
                            <span>No date available</span>
                          )}
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt className="mt-3 font-medium text-gray-900">Status</dt>
                      <dd className="mt-3 text-gray-500">
                        <span className="block">
                          {order?.status === 'CONFIRMED' ? (
                            <>
                              <p className="text-sm font-medium text-green-500">{order?.status}</p>
                            </>
                          ) : (
                            <>
                              <p className="text-sm font-medium text-yellow-500">{order?.status}</p>
                            </>
                          )}
                        </span>
                      </dd>
                    </div>
                    {order?.paymentDetails?.status && (
                      <div>
                        <dt className="mt-3 font-medium text-gray-900">Payment status</dt>
                        <dd className="mt-3 text-gray-500">
                          <span className="block">
                            {order?.paymentDetails?.status === 'paid' ? (
                              <>
                                <p className="text-sm font-medium text-green-500">
                                  {order?.paymentDetails?.status}
                                </p>
                              </>
                            ) : (
                              <>
                                <p className="text-sm font-medium text-yellow-500">
                                  {order?.paymentDetails?.status}
                                </p>
                              </>
                            )}
                          </span>
                        </dd>
                      </div>
                    )}
                  </dl>

                  <dl className="mt-8 divide-y divide-gray-200 text-sm lg:col-span-5 lg:mt-0 md:">
                    <div className="flex items-center justify-between pb-4">
                      <dt className="text-gray-600">Subtotal</dt>
                      <dd className="font-medium text-gray-900">
                        {order?.currencyCode}
                        <span className="ml-2">{order?.subTotalAmount?.toFixed(2)}</span>
                      </dd>
                    </div>
                    <div className="flex items-center justify-between py-4">
                      <dt className="text-gray-600">Total Tax</dt>
                      <dd className="font-medium text-gray-900">
                        {totalTaxAmount > 0 ? order?.currencyCode : ''}
                        <span className="ml-2">{totalTaxAmount > 0 ? totalTaxAmount?.toFixed(2) : 0}</span>
                      </dd>
                    </div>
                    <div className="flex items-center justify-between py-4">
                      <dt className="flex text-gray-600 ">
                        Discount
                        {order?.discountCode && (
                          <span className="ml-2 rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-600">
                            {order?.discountCode}
                          </span>
                        )}
                      </dt>
                      {order?.discountAmount ? (
                        <dd className="font-medium text-gray-900">
                          -<span className="ml-2">{order?.currencyCode}</span>
                          <span className="ml-2">{order?.discountAmount.toFixed(2)}</span>
                        </dd>
                      ) : (
                        <dd className="font-medium text-gray-900">0</dd>
                      )}
                    </div>
                    <div className="flex items-center justify-between pt-4">
                      <dt className="font-medium text-gray-900">Total</dt>
                      <dd className="font-medium text-indigo-600">
                        {order?.currencyCode}
                        <span className="ml-2">{order?.totalAmount.toFixed(2)}</span>
                      </dd>
                    </div>
                  </dl>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VendorOrderListingDetail;
